// Shelf System 3D Visualization Component
import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { SupportConfiguration } from './ShelfSupportCalculator';

interface ShelfSystemVisualization3DProps {
  shelfWidth: number;
  shelfDepth: number;
  shelfThickness: number;
  supportHeight: number;
  numberOfShelves: number;
  shelfSpacing?: number;
  supportConfig: SupportConfiguration;
  showDeflection?: boolean;
  showStress?: boolean;
}

export const ShelfSystemVisualization3D: React.FC<ShelfSystemVisualization3DProps> = ({
  shelfWidth,
  shelfDepth,
  shelfThickness,
  supportHeight,
  numberOfShelves,
  shelfSpacing = 400,
  supportConfig,
  showDeflection = false,
  showStress = false
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const frameIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a1a);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      50,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      10000
    );
    
    // Position camera to see the whole system
    const maxDim = Math.max(shelfWidth, shelfDepth, supportHeight);
    camera.position.set(maxDim * 1.5, maxDim * 1.2, maxDim * 1.5);
    camera.lookAt(0, supportHeight / 2, 0);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, supportHeight / 2, 0);
    controls.update();

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(500, 1000, 500);
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.near = 0.1;
    directionalLight.shadow.camera.far = 5000;
    directionalLight.shadow.camera.left = -1000;
    directionalLight.shadow.camera.right = 1000;
    directionalLight.shadow.camera.top = 1000;
    directionalLight.shadow.camera.bottom = -1000;
    scene.add(directionalLight);

    // Grid helper
    const gridHelper = new THREE.GridHelper(
      Math.max(shelfWidth, shelfDepth) * 1.5,
      20,
      0x444444,
      0x222222
    );
    scene.add(gridHelper);

    // Materials
    const shelfMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x88ccff,
      transparent: true,
      opacity: 0.7,
      roughness: 0.1,
      metalness: 0.1,
      clearcoat: 1.0,
      clearcoatRoughness: 0.1
    });

    const supportMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xffa500,
      roughness: 0.2,
      metalness: 0.3,
      clearcoat: 0.8,
      clearcoatRoughness: 0.2
    });

    // Create shelves
    const shelfGeometry = new THREE.BoxGeometry(shelfWidth, shelfThickness, shelfDepth);
    
    for (let i = 0; i < numberOfShelves; i++) {
      const shelf = new THREE.Mesh(shelfGeometry, shelfMaterial);
      const yPosition = i * shelfSpacing + shelfThickness / 2;
      shelf.position.set(0, yPosition, 0);
      shelf.castShadow = true;
      shelf.receiveShadow = true;
      
      // Add deflection visualization if enabled
      if (showDeflection && supportConfig.maxShelfDeflection > 0) {
        // Create a curved shelf to show deflection
        const curve = new THREE.CatmullRomCurve3([
          new THREE.Vector3(-shelfWidth/2, yPosition, 0),
          new THREE.Vector3(0, yPosition - supportConfig.maxShelfDeflection, 0),
          new THREE.Vector3(shelfWidth/2, yPosition, 0)
        ]);
        
        const points = curve.getPoints(50);
        const deflectionGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const deflectionLine = new THREE.Line(
          deflectionGeometry,
          new THREE.LineBasicMaterial({ color: 0xff0000, linewidth: 2 })
        );
        scene.add(deflectionLine);
      }
      
      scene.add(shelf);
    }

    // Create supports
    supportConfig.positions.forEach((pos, index) => {
      const supportGeometry = supportConfig.type === 'tube' 
        ? new THREE.CylinderGeometry(
            supportConfig.diameter / 2,
            supportConfig.diameter / 2,
            supportHeight,
            32
          )
        : new THREE.CylinderGeometry(
            supportConfig.diameter / 2,
            supportConfig.diameter / 2,
            supportHeight,
            32
          );

      // For tubes, create inner hole
      if (supportConfig.type === 'tube' && supportConfig.innerDiameter) {
        const innerGeometry = new THREE.CylinderGeometry(
          supportConfig.innerDiameter / 2,
          supportConfig.innerDiameter / 2,
          supportHeight + 1,
          32
        );
        
        // Create tube using CSG (simplified approach)
        const support = new THREE.Mesh(supportGeometry, supportMaterial);
        const inner = new THREE.Mesh(innerGeometry, supportMaterial);
        
        support.position.set(
          pos.x - shelfWidth / 2,
          supportHeight / 2,
          pos.y - shelfDepth / 2
        );
        support.castShadow = true;
        support.receiveShadow = true;
        scene.add(support);
      } else {
        // Solid rod
        const support = new THREE.Mesh(supportGeometry, supportMaterial);
        support.position.set(
          pos.x - shelfWidth / 2,
          supportHeight / 2,
          pos.y - shelfDepth / 2
        );
        support.castShadow = true;
        support.receiveShadow = true;
        scene.add(support);
      }

      // Add labels for support positions
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        canvas.width = 128;
        canvas.height = 64;
        context.fillStyle = 'white';
        context.font = '24px Arial';
        context.textAlign = 'center';
        context.fillText(`S${index + 1}`, 64, 40);
        
        const texture = new THREE.CanvasTexture(canvas);
        const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.set(
          pos.x - shelfWidth / 2,
          supportHeight + 50,
          pos.y - shelfDepth / 2
        );
        sprite.scale.set(50, 25, 1);
        scene.add(sprite);
      }
    });

    // Add stress visualization if enabled
    if (showStress) {
      supportConfig.positions.forEach((pos) => {
        const stressLevel = supportConfig.maxStressInSupport / 100; // Normalize
        const color = new THREE.Color();
        color.setHSL(0.3 - stressLevel * 0.3, 1, 0.5); // Green to Red
        
        const stressIndicator = new THREE.Mesh(
          new THREE.SphereGeometry(10, 16, 16),
          new THREE.MeshBasicMaterial({ color })
        );
        stressIndicator.position.set(
          pos.x - shelfWidth / 2,
          supportHeight / 2,
          pos.y - shelfDepth / 2
        );
        scene.add(stressIndicator);
      });
    }

    // Add dimension lines
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0x666666 });
    
    // Width dimension
    const widthPoints = [
      new THREE.Vector3(-shelfWidth/2, -50, -shelfDepth/2 - 50),
      new THREE.Vector3(shelfWidth/2, -50, -shelfDepth/2 - 50)
    ];
    const widthGeometry = new THREE.BufferGeometry().setFromPoints(widthPoints);
    const widthLine = new THREE.Line(widthGeometry, lineMaterial);
    scene.add(widthLine);

    // Depth dimension
    const depthPoints = [
      new THREE.Vector3(-shelfWidth/2 - 50, -50, -shelfDepth/2),
      new THREE.Vector3(-shelfWidth/2 - 50, -50, shelfDepth/2)
    ];
    const depthGeometry = new THREE.BufferGeometry().setFromPoints(depthPoints);
    const depthLine = new THREE.Line(depthGeometry, lineMaterial);
    scene.add(depthLine);

    // Animation loop
    const animate = () => {
      frameIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return;
      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [
    shelfWidth,
    shelfDepth,
    shelfThickness,
    supportHeight,
    numberOfShelves,
    shelfSpacing,
    supportConfig,
    showDeflection,
    showStress
  ]);

  return (
    <div 
      ref={mountRef} 
      className="w-full h-full rounded-lg overflow-hidden"
      style={{ minHeight: '400px' }}
    />
  );
};
